import { Inject, Injectable, OnDestroy, PLATFORM_ID } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, map, mergeMap, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class PageTitleService implements OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();
  private readonly defaultTitle = 'Pitch59';
  private currentTitle = this.defaultTitle;
  private titleObserver?: MutationObserver;
  private isBrowser: boolean;

  constructor(
    private title: Title,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.restoreNativeTitleSetter();
    this.startObservingTitle();
  }

  init(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.getPrimaryRoute(this.activatedRoute)),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
        takeUntil(this.destroy$)
      )
      .subscribe((data) => {
        if (data['managePageTitle'] === false) {
          return;
        }

        const rawTitle = data['title'];
        if (rawTitle) {
          this.translate.get(rawTitle).subscribe({
            next: (translated: string) => {
              this.setTitle(translated);
            },
            error: () => {
              this.setTitle(this.defaultTitle);
            }
          });
        } else {
          this.setTitle(this.defaultTitle);
        }
      });
  }

  setTitle(title: string): void {
    title = title.replace(/\s+/g, ' ').trim();
    if(this.currentTitle !== title){
      this.currentTitle = title; 
    }
    this.title.setTitle(title);

    // Only set document.title in browser environment
    if (this.isBrowser) {
      document.title = title;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.titleObserver?.disconnect();
  }

  private getPrimaryRoute(route: ActivatedRoute): ActivatedRoute {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  private restoreNativeTitleSetter(): void {
    // Only run in browser environment
    if (!this.isBrowser) {
      return;
    }

    try {
      const descriptor = Object.getOwnPropertyDescriptor(document, 'title');
      if (descriptor && descriptor.configurable) {
        delete (document as any).title;
      }
    } catch (e) {}
  }

  private startObservingTitle(): void {
    // Only run in browser environment
    if (!this.isBrowser) {
      return;
    }

    const titleTag = document.querySelector('title');
    if (!titleTag) {
      return;
    }

    this.titleObserver = new MutationObserver(() => {
      if (document.title !== this.currentTitle) {
        document.title = this.currentTitle;
      }
    });

    this.titleObserver.observe(titleTag, { childList: true });
  }
}
